/* eslint-disable @typescript-eslint/naming-convention */
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { interval, skip } from 'rxjs';
import { AuthSessionService } from '../auth-session/auth-session.service';
import { ToolsService } from '../tools/tools.service';
import { UserLanguageSpoken } from '@app/core/models';

declare const window: any;

const languagesSpokenMap: Record<string, string> = {
  en: 'English',
  fr: 'French',
  de: 'German',
  es: 'Spanish',
  it: 'Italian',
  'pt-br': 'Portuguese',
  pt: 'Portuguese',
};

/**
 * Service to integrate Brevo (Sendinblue) tracking in Angular.
 * Handles initialization, re-identification on user change,
 * and route change tracking.
 */
@Injectable({ providedIn: 'root' })
export class BrevoService {
  // Injects
  private readonly authSessionService = inject(AuthSessionService);
  private readonly toolsService = inject(ToolsService);
  private readonly router = inject(Router);

  // Config
  private readonly sibStorageKey = 'sib_mail';
  private readonly sibId = 'sendinblue-js';
  private readonly brevoLoaderId = 'brevo-sdk-loader';

  // Vars
  private initing = false;
  private trackingStarted = false;
  private sib: any;
  private needTotrakPageAfterInit = false;

  /**
   * Starts Brevo tracking, listens to auth changes and route changes.
   */
  startTracking(): void {
    if (this.trackingStarted) {
      return;
    }
    this.trackingStarted = true;

    if (!this.sib && !this.initing) {
      this.init();
    }

    // React to user profile changes
    this.authSessionService.currentUserProfile$.pipe(skip(1)).subscribe((user) => {
      if (!user?.mail) {
        this.stopTracking();
        return;
      }
      this.reinitOrIdentify();
    });

    // setTimeout(() => {
    //   this.trackPage();
    // }, 200);

    // Track route changes after title is likely updated
    // this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
    //   if (this.sib && this.sib.page) {
    //     setTimeout(() => {
    //       this.sib.page();
    //     }, 200);
    //   }
    // });
  }

  /**
   * Stops Brevo tracking and remove loaded libs.
   */
  stopTracking(): void {
    if (!this.trackingStarted) {
      return;
    }
    document.getElementById(this.sibId)?.remove();
    document.getElementById(this.brevoLoaderId)?.remove();

    this.deleteSibCookie();

    delete window.sib;
    delete window.sendinblue;
    delete window.Brevo;
    delete this.sib;

    this.trackingStarted = false;
    localStorage.removeItem(this.sibStorageKey);
  }

  trackPage(): void {
    if (!this.trackingStarted) {
      return;
    }
    if (!this.sib) {
      this.needTotrakPageAfterInit = true;
      return;
    }
    this.sib.page();
  }

  trackManuallyPage(pageName: string, title: string): void {
    if (!this.trackingStarted) {
      return;
    }
    this.sib.push([
      'page',
      pageName,
      {
        ma_title: title,
      },
    ]);
  }

  /**
   * Initializes the Brevo script and tracking setup.
   */
  private init(): void {
    if (this.sib || this.initing) return;
    this.initing = true;

    // Set up global Brevo array and initial page call
    window.Brevo = window.Brevo || [];
    window.Brevo.push([
      'init',
      {
        client_key: environment.brevoClientKey,
        do_not_track_page: true,
      },
    ]);

    const userData = this.getUserData();
    if (userData?.identifiers.email_id) {
      window.Brevo.push(['identify', userData]);
    }

    this.toolsService
      .loadScript('https://cdn.brevo.com/js/sdk-loader.js', {
        id: this.brevoLoaderId,
        async: 'true',
      })
      .subscribe({
        next: () => {
          // Wait until Brevo is fully loaded using interval
          const sub = interval(1000).subscribe(() => {
            if (window.Brevo && window.Brevo.page) {
              // Wait a little extra to be safe as the sib script takes time to load
              setTimeout(() => {
                this.sib = window.Brevo;
                if (userData) {
                  localStorage.setItem(this.sibStorageKey, userData.identifiers.email_id);
                }
                this.initing = false;
                if (this.needTotrakPageAfterInit) {
                  this.sib.page();
                }
                this.trackingStarted = true;
                sub.unsubscribe();
              }, 100);
            }
          });
        },
      });
  }

  /**
   * Deletes the sib_cuid cookie from all domain levels.
   */
  private deleteSibCookie(): void {
    const match = document.cookie.match(/(?:^|;\s*)sib_cuid=[^;]*/);
    if (!match) return;

    const domainParts = location.hostname.split('.');
    domainParts.slice(0, -1).forEach((_, i) => {
      const domain = '.' + domainParts.slice(i).join('.');
      document.cookie = `sib_cuid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
    });

    // Try deleting without domain
    document.cookie = 'sib_cuid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  }

  /**
   * Reinitializes the Brevo SDK and resets internal state.
   */
  private reInit(): void {
    this.stopTracking();
    this.init();
  }

  /**
   * Extracts user data from the AuthSessionService.
   */
  private getUserData(): any | undefined {
    const user = this.authSessionService.currentUserProfile;
    if (!user?.mail) return;

    return {
      identifiers: { email_id: user.mail },
      attributes: {
        FIRSTNAME: user.field_first_name,
        LASTNAME: user.field_surname,
        // WHATSAPP: user.field_phone,
        ROLE_PLAN: user.current_role?.id,
        EXPIRY_DATE: user.subscription_expiry,
        TOP_QUALIFICATION: user.top_qualification,
        EXT_ID: user.uid,
        SPOKEN_LANGUAGES: this.convertLanguagesSpoken(user.field_language_spoken),
        FAVORITELANGUAGE: user.preferred_langcode?.name,
      },
    };
  }

  /**
   * Reidentifies the user or reinitializes if identity changed.
   */
  private reinitOrIdentify(): void {
    // const user = this.authSessionService.currentUserProfile;
    // const currentMail = this.getUserData()?.identifiers.email_id;
    // const storedMail = localStorage.getItem(this.sibStorageKey);
    // if (storedMail !== currentMail) {
    if (this.authSessionService.isLogged && window.Brevo) {
      this.trackingStarted = true;
      window.Brevo?.push(['identify', this.getUserData()]);
      //localStorage.setItem(this.sibStorageKey, user.mail);
    } else {
      this.reInit();
    }
    //}
  }

  /**
   * Converts an array of `UserLanguageSpoken` objects into an array of unique language names.
   *
   * Iterates through the provided `languagesSpoken` array, maps each language code to its corresponding
   * language name using the `languagesSpokenMap`, and collects unique language names in the result array.
   *
   * @param languagesSpoken - An array of `UserLanguageSpoken` objects to be converted.
   * @returns An array of unique language names corresponding to the input language codes.
   */
  private convertLanguagesSpoken(languagesSpoken: UserLanguageSpoken[]): string[] {
    const languages: string[] = [];
    languagesSpoken.forEach((lang) => {
      const langName = languagesSpokenMap[lang.code];
      if (langName && !languages.includes(langName)) {
        languages.push(langName);
      }
    });
    return languages;
  }
}
